import React, { useState, useCallback } from 'react';
import { string, array, func } from 'prop-types';
import { ResponsiveImage, AspectRatioWrapper, Modal } from '../../../../components';
import classNames from 'classnames';

import {
  PrevArrow as ArrowLeft,
  NextArrow as ArrowRight,
} from '../../../../components/Slider/Arrows';
import ImageCarousel from './ImageCarousel/ImageCarousel';
import css from './ImageSlider.module.css';

/**
 * Component for displaying a slider of images with pagination, navigation buttons,
 * and a modal view for expanded viewing.
 */
const ImageSlider = props => {
  const { rootClassName, className, images, title, onManageDisableScrolling } = props;

  // State
  const [index, setIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const total = images.length;

  /**
   * Navigate to the previous image
   */
  const prev = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setIndex(prevIndex => (prevIndex - 1 + total) % total);
    },
    [total]
  );

  /**
   * Navigate to the next image
   */
  const next = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setIndex(prevIndex => (prevIndex + 1) % total);
    },
    [total]
  );

  /**
   * Jump to a specific image index
   */
  const goToIndex = useCallback(
    i => e => {
      e.preventDefault();
      e.stopPropagation();
      setIndex(i);
    },
    []
  );

  /**
   * Open the modal view
   */
  const openModal = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setIsModalOpen(true);
      onManageDisableScrolling('ImageSliderModal', true);
    },
    [onManageDisableScrolling]
  );

  /**
   * Close the modal view
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    onManageDisableScrolling('ImageSliderModal', false);
  }, [onManageDisableScrolling]);

  const classes = classNames(rootClassName || css.root, className);
  const isFirstSlide = index === 0;
  const isLastSlide = index === total - 1;

  return (
    <>
      <AspectRatioWrapper width={4} height={3} className={classes}>
        <div className={css.sliderWrapper}>
          <div
            className={css.sliderTrack}
            style={{ transform: `translateX(-${index * 100}%)` }}
            onClick={openModal}
            role="button"
            tabIndex={0}
            aria-label={`View ${title || 'image'} gallery`}
          >
            {images.map((img, i) => {
              const url = img?.attributes?.variants?.default?.url;
              return (
                <div key={i} className={css.slide}>
                  <div
                    className={css.imageBackground}
                    style={{ backgroundImage: `url(${url})` }}
                    aria-hidden="true"
                  />
                  <ResponsiveImage
                    rootClassName={css.imageForeground}
                    alt={`${title || 'Image'} ${i + 1} of ${total}`}
                    image={img}
                    variants={['default']}
                  />
                </div>
              );
            })}
          </div>

          {/* Navigation buttons - only show if multiple images */}
          {total > 1 && (
            <div className={css.navButtons}>
              <ArrowLeft
                onClick={prev}
                className={classNames(css.navButton, { [css.disabled]: isFirstSlide })}
                aria-label="Previous image"
                disabled={isFirstSlide}
              />
              <ArrowRight
                onClick={next}
                className={classNames(css.navButton, { [css.disabled]: isLastSlide })}
                aria-label="Next image"
                disabled={isLastSlide}
              />
            </div>
          )}

          {/* Pagination dots - only show if multiple images */}
          {total > 1 && (
            <div className={css.dotsWrapper} role="tablist" aria-label="Image pagination">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={classNames(css.dot, { [css.activeDot]: i === index })}
                  onClick={goToIndex(i)}
                  role="tab"
                  tabIndex={0}
                  aria-selected={i === index}
                  aria-label={`Go to image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </AspectRatioWrapper>

      {/* Modal carousel for expanded viewing */}
      <Modal
        id="ImageSliderModal"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={isModalOpen}
        onClose={closeModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel
          images={images}
          selectedImage={images[index]}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </Modal>
    </>
  );
};

ImageSlider.defaultProps = {
  rootClassName: null,
  className: null,
  title: null,
  onManageDisableScrolling: () => {},
};

ImageSlider.propTypes = {
  // Styling props
  rootClassName: string,
  className: string,

  // Content props
  images: array.isRequired,
  title: string,

  // Functional props
  onManageDisableScrolling: func,
};

export default ImageSlider;
